import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip as RTooltip,
  CartesianGrid, BarChart, Bar, Cell,
} from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useDataScope } from "@/hooks/useDataScope";
import StatusBadge from "@/components/StatusBadge";
import UsShipmentMap from "@/components/UsShipmentMap";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  Package, Truck, RefreshCw, ArrowUpRight, ArrowDownRight, Weight,
  MapPin, ExternalLink, Boxes, CalendarRange, X,
} from "lucide-react";
import { formatDateTime, formatDate, formatStamp } from "@/lib/format";

const LAST_EVENT_POLL_MS = 30_000;
const RANGES = [
  { value: 1, label: "Today" },
  { value: 15, label: "15 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
];

export default function Dashboard({ scopeAccountId = null }) {
  const { user, account } = useAuth();
  const scope = useDataScope();
  // Optional opt-in override: when a fixed account id is passed (e.g. the cockpit's
  // single-store preview), scope every fetch to that store instead of the global viewAs.
  const param = scopeAccountId ? `account_id=${encodeURIComponent(scopeAccountId)}` : scope.param;
  const allMode = scopeAccountId ? false : scope.allMode;
  const { isAdmin } = scope;

  const [range, setRange] = useState(30);
  // Custom date range (ship-date based, may cross months): {from: Date, to: Date} | null
  const [customRange, setCustomRange] = useState(null);
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const lastEventRef = useRef(null);
  const reqIdRef = useRef(0);

  const customKey = customRange ? `${format(customRange.from, "yyyy-MM-dd")}_${format(customRange.to, "yyyy-MM-dd")}` : "";

  const load = useCallback(async ({ silent } = {}) => {
    const id = ++reqIdRef.current;
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const base = customRange
        ? `start=${format(customRange.from, "yyyy-MM-dd")}&end=${format(customRange.to, "yyyy-MM-dd")}`
        : `range=${range}`;
      const q = `${base}${param ? `&${param}` : ""}`;
      const { data } = await api.get(`/dashboard/summary?${q}${silent ? "&refresh=true" : ""}`);
      if (id === reqIdRef.current) setS(data);
    } finally {
      if (id === reqIdRef.current) {
        if (silent) setRefreshing(false); else setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, param, customKey]);

  const applyCustomRange = (r) => { setCustomRange(r); };
  const clearCustomRange = () => { setCustomRange(null); };
  const selectPreset = (v) => { setCustomRange(null); setRange(v); };

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isAdmin) return; // no live polling for admin aggregate / impersonation views
    let cancelled = false;
    const i = setInterval(async () => {
      try {
        const { data } = await api.get("/dashboard/last-event");
        if (cancelled) return;
        const stamp = data.last_event_at;
        if (stamp && stamp !== lastEventRef.current) {
          if (lastEventRef.current !== null) load({ silent: true });
          lastEventRef.current = stamp;
        }
      } catch (err) {
        // Background poll — log at debug level so it doesn't spam, but isn't fully swallowed.
        console.debug("dashboard last-event poll failed", err);
      }
    }, LAST_EVENT_POLL_MS);
    return () => { cancelled = true; clearInterval(i); };
  }, [load, isAdmin]);

  if (loading) return (
    <div data-testid="dashboard-loading" className="space-y-4">
      <div className="flex items-center gap-2 text-bh-body">
        <RefreshCw className="w-4 h-4 animate-spin text-bh-blue" />
        <span className="text-sm font-medium">Crunching fulfillment analytics{allMode ? " across all customers" : ""}…</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="bg-white border border-bh-divider rounded-xl p-5 min-h-[140px] animate-pulse">
            <div className="h-3 w-24 bg-slate-100 rounded" />
            <div className="h-8 w-16 bg-slate-100 rounded mt-4" />
            <div className="h-3 w-32 bg-slate-100 rounded mt-3" />
          </div>
        ))}
      </div>
      <div className="bg-white border border-bh-divider rounded-xl p-6 h-64 animate-pulse" />
    </div>
  );
  if (!s) return <div className="text-bh-muted">Unable to load dashboard.</div>;

  const firstName = (user?.name || "").split(" ")[0];
  const { kpis, carrier_breakdown, shipping_velocity, weight_trend, status_breakdown, time_to_ship, destination_map, top_states, recent_shipments, impersonating } = s;
  const presetLabel = RANGES.find((r) => r.value === range)?.label || `${range} days`;
  const rangeLabel = customRange ? (s.range_label || customKey.replace("_", " → ")) : presetLabel;
  const headerPeriod = customRange
    ? `${format(customRange.from, "MMM d")} – ${format(customRange.to, "MMM d, yyyy")}`
    : (presetLabel === "Today" ? "today" : `last ${presetLabel}`);
  const companyName = allMode ? "all accounts" : (impersonating?.company_name || account?.company_name || "your account");

  return (
    <div data-testid="dashboard-page" className="space-y-7">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Chip>Shipments</Chip>
            <Chip>Carriers</Chip>
            <Chip>Destinations</Chip>
            <Chip>Volume</Chip>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-bh-navy-deep" style={{ fontFamily: "Manrope" }}>
            Good {greeting()}, <span className="text-bh-orange">{firstName}</span> <span className="inline-block">👋</span>
          </h1>
          <p className="text-bh-body mt-2">Fulfillment analytics for {companyName} — {headerPeriod} <span className="text-bh-muted">(by ship date)</span>.</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <RangeSelector value={range} onChange={selectPreset} custom={customRange} />
          <CustomRangePicker value={customRange} onApply={applyCustomRange} onClear={clearCustomRange} />
          <button
            data-testid="refresh-dashboard-btn"
            onClick={() => load({ silent: true })}
            disabled={refreshing}
            className="hidden sm:inline-flex items-center gap-2 text-xs font-semibold text-bh-body hover:text-bh-blue bg-white border border-bh-divider px-3 py-2 rounded-md transition-colors disabled:opacity-60"
            title={s.cached_at ? `Updated as of ${formatStamp(s.cached_at)} — click to pull the newest shipments` : "Refresh now"}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "…" : "Refresh"}
            <span className={`w-1.5 h-1.5 rounded-full ${refreshing ? "bg-bh-orange" : "bg-green-500"}`}></span>
          </button>
        </div>
      </div>

      {s.cached_at && (
        <p className="text-xs text-bh-muted -mt-3" data-testid="dashboard-cached-note">
          Updated as of {formatStamp(s.cached_at)} · press Refresh for the newest shipments
        </p>
      )}

      {/* KPI row — volume only, no money */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        <KpiTile
          testId="kpi-shipped" icon={Truck} accent="orange"
          label={`Shipments shipped`}
          value={kpis.shipments.total.toLocaleString()}
          sub={kpis.shipments.delta_pct !== null && kpis.shipments.delta_pct !== undefined
            ? <Delta pct={kpis.shipments.delta_pct} rangeLabel={rangeLabel} />
            : `${kpis.shipments.per_day_avg}/day average`}
        />
        <KpiTile
          testId="kpi-per-day" icon={Boxes} accent="blue"
          label="Avg shipments / day"
          value={kpis.shipments.per_day_avg.toLocaleString()}
          sub={`Across ${carrier_breakdown.length} carrier${carrier_breakdown.length === 1 ? "" : "s"}`}
        />
        <KpiTile
          testId="kpi-orders" icon={Package} accent="violet"
          label="Orders shipped"
          value={kpis.orders.total.toLocaleString()}
          sub={`${kpis.orders.avg_units_per_order ?? 0} units/order avg · ${kpis.orders.open ?? 0} open`}
        />
        <KpiTile
          testId="kpi-weight" icon={Weight} accent="green"
          label="Avg weight / order"
          value={`${kpis.shipments.avg_weight} lbs`}
          sub={`Across ${kpis.shipments.total.toLocaleString()} shipment${kpis.shipments.total === 1 ? "" : "s"}`}
        />
      </div>

      {/* Velocity area chart */}
      <Card title="Shipping Velocity" subtitle={`Shipments shipped per day — ${rangeLabel === "Today" ? "today" : `last ${rangeLabel}`}`} testId="velocity-card">
        <VelocityChart data={shipping_velocity} />
      </Card>

      {/* Carriers + Destination map */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2">
          <Card title="Carrier Mix" subtitle={`${carrier_breakdown.reduce((a, c) => a + c.count, 0)} shipments · ${carrier_breakdown.length} carrier${carrier_breakdown.length === 1 ? "" : "s"}`} testId="carrier-mix-card">
            {carrier_breakdown.length === 0 ? (
              <EmptyState text="No completed shipments in this window." />
            ) : (
              <>
                <CarrierDonut breakdown={carrier_breakdown} />
                <ul className="space-y-2.5 mt-5">
                  {carrier_breakdown.map((c) => <CarrierRow key={c.carrier} c={c} />)}
                </ul>
              </>
            )}
          </Card>
        </div>

        <div className="lg:col-span-3">
          <Card title="Where it's going" subtitle="Completed shipment destinations across the US" testId="destination-map-card">
            {destination_map.length === 0 ? (
              <EmptyState text="No mapped destinations yet." />
            ) : (
              <UsShipmentMap points={destination_map} />
            )}
            {top_states?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-bh-divider">
                {top_states.map((st) => (
                  <span key={st.state} data-testid={`top-state-${st.state}`} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-bh-bg text-bh-navy-deep px-2.5 py-1 rounded-full">
                    <MapPin className="w-3 h-3 text-bh-orange" /> {st.state} <span className="text-bh-muted">{st.count}</span>
                  </span>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Status breakdown + Weight trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Order Pipeline" subtitle="Open orders not yet shipped (live)" testId="status-card">
          {status_breakdown?.length === 0 ? (
            <EmptyState text="No open orders — all caught up." />
          ) : (
            <StatusChart data={status_breakdown} />
          )}
        </Card>

        <Card title="Average Order Weight" subtitle="Average weight per order shipped, per day" testId="weight-card">
          {(!weight_trend || weight_trend.every((w) => w.weight === 0)) ? (
            <EmptyState text="No weight data in this window." />
          ) : (
            <WeightTrend data={weight_trend} />
          )}
        </Card>
      </div>

      {/* Order size + Top destination states */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Order Size" subtitle={`Units per order shipped · ${s.order_size?.avg_units_per_order ?? 0} avg across ${(s.order_size?.sample_count ?? 0).toLocaleString()} shipped`} testId="order-size-card">
          {(!s.order_size || s.order_size.sample_count === 0) ? (
            <EmptyState text="Nothing shipped in this window." />
          ) : (
            <OrderSizeChart data={s.order_size.distribution} />
          )}
        </Card>

        <Card title="Top Destination States" subtitle="Where your shipments are going — by volume" testId="top-states-card">
          {(!top_states || top_states.length === 0) ? (
            <EmptyState text="No mapped destinations yet." />
          ) : (
            <TopStatesBars data={top_states} />
          )}
        </Card>
      </div>

      {/* Time to ship (order placed → shipped) */}
      <Card title="Time to Ship" subtitle="How fast orders leave the door — order placed → shipped (no cost involved)" testId="time-to-ship-card">
        {(!time_to_ship || time_to_ship.sample_count === 0) ? (
          <EmptyState text="Not enough matched orders + shipments in this window yet." />
        ) : (
          <TimeToShip data={time_to_ship} />
        )}
      </Card>

      {/* Recent shipments */}
      <Card
        title="Recent Shipments"
        rightSlot={<Link to="/orders-shipments" className="text-sm text-bh-blue hover:text-bh-blue-dark font-semibold">View all →</Link>}
        testId="recent-shipments-card"
      >
        {recent_shipments.length === 0 ? (
          <EmptyState text="No shipments yet." />
        ) : (
          <ul className="divide-y divide-bh-divider" data-testid="dashboard-recent-shipments">
            {recent_shipments.map((sh) => <ShipmentRow key={sh.id} sh={sh} />)}
          </ul>
        )}
      </Card>
    </div>
  );
}

/* ---------- Range selector ---------- */
function RangeSelector({ value, onChange, custom }) {
  return (
    <div data-testid="range-selector" className="inline-flex bg-white border border-bh-divider rounded-lg p-1">
      {RANGES.map((r) => (
        <button
          key={r.value}
          data-testid={`range-${r.value}`}
          onClick={() => onChange(r.value)}
          className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
            !custom && value === r.value ? "bg-bh-blue text-white" : "text-bh-body hover:bg-bh-bg"
          }`}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

function CustomRangePicker({ value, onApply, onClear }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || undefined);

  const apply = () => {
    if (draft?.from && draft?.to) {
      onApply({ from: draft.from, to: draft.to });
      setOpen(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-2">
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setDraft(value || undefined); }}>
        <PopoverTrigger asChild>
          <button
            data-testid="custom-range-btn"
            className={`inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-md border transition-colors ${
              value ? "bg-bh-blue text-white border-bh-blue" : "bg-white text-bh-body border-bh-divider hover:bg-bh-bg"
            }`}
          >
            <CalendarRange className="w-3.5 h-3.5" />
            {value ? `${format(value.from, "MMM d")} – ${format(value.to, "MMM d")}` : "Custom range"}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto p-0" data-testid="custom-range-popover">
          <div className="p-3 border-b border-bh-divider">
            <p className="text-sm font-semibold text-bh-navy-deep">Pick a date range</p>
            <p className="text-xs text-bh-muted">By ship date — can cross months.</p>
          </div>
          <Calendar
            mode="range"
            numberOfMonths={2}
            selected={draft}
            onSelect={setDraft}
            defaultMonth={value?.from}
            disabled={{ after: new Date() }}
            data-testid="custom-range-calendar"
          />
          <div className="flex items-center justify-between gap-2 p-3 border-t border-bh-divider">
            <span className="text-xs text-bh-muted">
              {draft?.from ? format(draft.from, "MMM d, yyyy") : "Start"} → {draft?.to ? format(draft.to, "MMM d, yyyy") : "End"}
            </span>
            <button
              data-testid="custom-range-apply-btn"
              onClick={apply}
              disabled={!draft?.from || !draft?.to}
              className="text-xs font-semibold bg-bh-blue text-white px-4 py-1.5 rounded-md disabled:opacity-50 hover:bg-bh-blue-dark transition-colors"
            >
              Apply
            </button>
          </div>
        </PopoverContent>
      </Popover>
      {value && (
        <button
          data-testid="custom-range-clear-btn"
          onClick={onClear}
          className="text-bh-muted hover:text-bh-navy-deep p-1.5 rounded-md hover:bg-bh-bg transition-colors"
          title="Clear custom range"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

/* ---------- Shared recharts style constants (stable refs → no re-render churn) ---------- */
const CHART_MARGIN = { top: 8, right: 8, left: 0, bottom: 0 };
const AXIS_TICK = { fontSize: 11, fill: "#7A8E9B" };
const AXIS_TICK_SM = { fontSize: 10, fill: "#7A8E9B" };
const ACTIVE_DOT = { r: 4 };
const BAR_CURSOR = { fill: "#F0F2F1" };
const BAR_RADIUS = [6, 6, 0, 0];

/* ---------- Velocity area chart (recharts) ---------- */
function VelocityChart({ data }) {
  const total = data.reduce((a, d) => a + d.count, 0);
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div data-testid="velocity-chart">
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={data} margin={CHART_MARGIN}>
          <defs>
            <linearGradient id="velFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#406CB4" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#406CB4" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#EDF1F0" vertical={false} />
          <XAxis dataKey="date" tickFormatter={(d) => formatDate(d)} tick={AXIS_TICK} interval="preserveStartEnd" minTickGap={36} axisLine={false} tickLine={false} />
          <YAxis allowDecimals={false} tick={AXIS_TICK} axisLine={false} tickLine={false} width={36} />
          <RTooltip content={<ChartTip unit="shipment" />} />
          <Area type="monotone" dataKey="count" stroke="#406CB4" strokeWidth={2.5} fill="url(#velFill)" dot={false} activeDot={ACTIVE_DOT} />
        </AreaChart>
      </ResponsiveContainer>
      <div className="flex items-center justify-between text-xs text-bh-muted mt-1 px-1">
        <span>{formatDate(data[0]?.date)}</span>
        <span className="text-bh-body font-semibold">{total} total · peak {max}/day</span>
        <span>{formatDate(data[data.length - 1]?.date)}</span>
      </div>
    </div>
  );
}

/* ---------- Weight trend ---------- */
function WeightTrend({ data }) {
  return (
    <div data-testid="weight-chart">
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={data} margin={CHART_MARGIN}>
          <defs>
            <linearGradient id="wFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#F9AA25" stopOpacity={0.4} />
              <stop offset="100%" stopColor="#F9AA25" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#EDF1F0" vertical={false} />
          <XAxis dataKey="date" tickFormatter={(d) => formatDate(d)} tick={AXIS_TICK} interval="preserveStartEnd" minTickGap={36} axisLine={false} tickLine={false} />
          <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} width={36} />
          <RTooltip content={<ChartTip unit="lb" valueKey="weight" />} />
          <Area type="monotone" dataKey="weight" stroke="#F9AA25" strokeWidth={2.5} fill="url(#wFill)" dot={false} activeDot={ACTIVE_DOT} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ---------- Order status bar chart ---------- */
const STATUS_COLORS = ["#406CB4", "#F9AA25", "#2E7D6F", "#8E44AD", "#7A8E9B", "#D4202C"];
function StatusChart({ data }) {
  return (
    <div data-testid="status-chart">
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} margin={CHART_MARGIN}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EDF1F0" vertical={false} />
          <XAxis dataKey="status" tick={AXIS_TICK_SM} axisLine={false} tickLine={false} interval={0} />
          <YAxis allowDecimals={false} tick={AXIS_TICK} axisLine={false} tickLine={false} width={36} />
          <RTooltip content={<ChartTip unit="order" />} cursor={BAR_CURSOR} />
          <Bar dataKey="count" radius={BAR_RADIUS}>
            {data.map((d, i) => <Cell key={d.status} fill={STATUS_COLORS[i % STATUS_COLORS.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ---------- Order size distribution bar chart ---------- */
const ORDER_SIZE_COLORS = ["#406CB4", "#5B8DD6", "#F9AA25", "#2E7D6F", "#8E44AD"];
function OrderSizeChart({ data }) {
  return (
    <div data-testid="order-size-chart">
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} margin={CHART_MARGIN}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EDF1F0" vertical={false} />
          <XAxis dataKey="bucket" tick={AXIS_TICK_SM} axisLine={false} tickLine={false} interval={0} />
          <YAxis allowDecimals={false} tick={AXIS_TICK} axisLine={false} tickLine={false} width={36} />
          <RTooltip content={<ChartTip unit="order" />} cursor={BAR_CURSOR} />
          <Bar dataKey="count" radius={BAR_RADIUS}>
            {data.map((d, i) => <Cell key={d.bucket} fill={ORDER_SIZE_COLORS[i % ORDER_SIZE_COLORS.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <p className="text-xs text-bh-muted text-center mt-1">Number of orders by units per order</p>
    </div>
  );
}

/* ---------- Top destination states horizontal bars ---------- */
function TopStatesBars({ data }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <ul className="space-y-2.5" data-testid="top-states-bars">
      {data.map((d) => {
        const w = (d.count / max) * 100;
        return (
          <li key={d.state} data-testid={`top-state-bar-${d.state}`} className="flex items-center gap-3">
            <span className="text-xs font-bold text-bh-navy-deep w-9 shrink-0 inline-flex items-center gap-1">
              <MapPin className="w-3 h-3 text-bh-orange" />{d.state}
            </span>
            <div className="flex-1 h-5 bg-bh-bg rounded-md overflow-hidden">
              <div className="h-full rounded-md transition-all duration-500 bg-bh-blue" style={{ width: `${Math.max(w, 3)}%` }} />
            </div>
            <span className="text-xs text-bh-muted w-10 text-right shrink-0">{d.count}</span>
          </li>
        );
      })}
    </ul>
  );
}


const TTS_COLORS = { "Same day": "#2E7D6F", "1 day": "#406CB4", "2 days": "#5B8DD6", "3-4 days": "#F9AA25", "5+ days": "#D4202C" };
function TimeToShip({ data }) {
  const maxBucket = Math.max(1, ...data.distribution.map((d) => d.count));
  return (
    <div data-testid="time-to-ship" className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
      <div className="text-center md:text-left md:border-r md:border-bh-divider md:pr-6">
        <p className="text-sm font-medium text-bh-muted">Average time to ship</p>
        <p className="text-5xl font-bold text-bh-navy-deep mt-1" style={{ fontFamily: "Manrope" }}>
          {data.avg_days}<span className="text-2xl text-bh-muted ml-1">{data.avg_days === 1 ? "day" : "days"}</span>
        </p>
        <p className="text-sm text-bh-muted mt-2">
          ≈ {data.avg_hours} hrs · <span className="font-semibold text-bh-navy-deep">{data.same_day_pct}%</span> same-day
        </p>
        <p className="text-xs text-bh-muted mt-1">Based on {data.sample_count.toLocaleString()} matched order{data.sample_count === 1 ? "" : "s"}</p>
      </div>
      <div className="md:col-span-2 space-y-2.5">
        {data.distribution.map((d) => {
          const pct = Math.round((d.count / data.sample_count) * 100);
          const w = (d.count / maxBucket) * 100;
          return (
            <div key={d.bucket} data-testid={`tts-${d.bucket.replace(/\s+/g, "-")}`} className="flex items-center gap-3">
              <span className="text-xs font-semibold text-bh-body w-16 shrink-0">{d.bucket}</span>
              <div className="flex-1 h-5 bg-bh-bg rounded-md overflow-hidden">
                <div className="h-full rounded-md transition-all duration-500" style={{ width: `${Math.max(w, d.count > 0 ? 3 : 0)}%`, backgroundColor: TTS_COLORS[d.bucket] || "#7A8E9B" }} />
              </div>
              <span className="text-xs text-bh-muted w-20 text-right shrink-0">{d.count} · {pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function ChartTip({ active, payload, label, unit, valueKey = "count" }) {
  if (!active || !payload?.length) return null;
  const v = payload[0].payload[valueKey];
  const lbl = payload[0].payload.status || formatDate(label);
  return (
    <div className="bg-bh-navy-deep text-white text-xs rounded-md px-2.5 py-1.5 shadow-lg">
      <div className="font-semibold">{lbl}</div>
      <div className="text-bh-orange font-bold">{v} {unit}{v === 1 ? "" : "s"}</div>
    </div>
  );
}

/* ---------- Carrier donut ---------- */
function CarrierDonut({ breakdown }) {
  const total = breakdown.reduce((a, c) => a + c.count, 0) || 1;
  let acc = 0;
  const segments = breakdown.map((c) => {
    const start = acc / total; acc += c.count; const end = acc / total;
    return { ...c, start, end };
  });
  const R = 64, CX = 80, CY = 80, STROKE = 22;
  return (
    <div className="flex items-center justify-center">
      <svg width="160" height="160" viewBox="0 0 160 160">
        <circle cx={CX} cy={CY} r={R} fill="none" stroke="#F0F2F1" strokeWidth={STROKE} />
        {segments.map((seg) => {
          const c = 2 * Math.PI * R;
          const len = (seg.end - seg.start) * c;
          const offset = -seg.start * c;
          return (
            <circle key={seg.carrier} cx={CX} cy={CY} r={R} fill="none" stroke={seg.bg || "#7A8E9B"} strokeWidth={STROKE}
              strokeDasharray={`${len} ${c - len}`} strokeDashoffset={offset} transform={`rotate(-90 ${CX} ${CY})`} />
          );
        })}
        <text x="50%" y="48%" textAnchor="middle" className="fill-bh-navy-deep" style={{ fontFamily: "Manrope", fontWeight: 700, fontSize: "26px" }}>{total}</text>
        <text x="50%" y="62%" textAnchor="middle" className="fill-bh-muted" style={{ fontSize: "10px", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>shipments</text>
      </svg>
    </div>
  );
}

function CarrierRow({ c }) {
  return (
    <li data-testid={`carrier-${c.carrier}`} className="flex items-center gap-3">
      <CarrierBadge carrier={c} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between text-sm">
          <span className="font-semibold text-bh-navy-deep truncate">{c.carrier}</span>
          <span className="text-bh-muted text-xs shrink-0 ml-2">{c.count} · {c.pct}%</span>
        </div>
        <div className="mt-1 h-1.5 bg-bh-bg rounded-full overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${c.pct}%`, backgroundColor: c.bg || "#7A8E9B" }} />
        </div>
        {(c.services || []).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`carrier-services-${c.carrier}`}>
            {c.services.slice(0, 3).map((s) => (
              <span key={s.code || s.label} title={`${s.label} · ${s.count.toLocaleString()} shipments`}
                className="inline-flex items-center gap-1 text-[10px] bg-bh-bg text-bh-navy-deep rounded-full px-2 py-0.5">
                {s.label}<span className="text-bh-muted tabular-nums">{s.count.toLocaleString()}</span>
              </span>
            ))}
            {c.services.length > 3 && <span className="text-[10px] text-bh-muted">+{c.services.length - 3} more</span>}
          </div>
        )}
      </div>
    </li>
  );
}

function CarrierBadge({ carrier: c }) {
  return (
    <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0 font-bold text-[11px] tracking-wide"
      style={{ backgroundColor: c.bg || "#7A8E9B", color: c.fg || "#FFFFFF", border: c.border ? `2px solid ${c.border}` : undefined }}
      title={c.carrier}>
      {c.short || (c.carrier || "?").slice(0, 4).toUpperCase()}
    </div>
  );
}

/* ---------- Recent shipment row (no cost) ---------- */
function ShipmentRow({ sh }) {
  return (
    <li data-testid={`shipment-row-${sh.id}`} className="py-3 flex items-center gap-3">
      <CarrierBadge carrier={{ carrier: sh.carrier_display || sh.carrier, bg: sh.bg, fg: sh.fg, short: sh.short, border: sh.border }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-bh-navy-deep text-sm">{sh.order_number}</span>
          <span className="text-bh-muted text-xs">{sh.service}</span>
          <StatusBadge status={sh.status} />
        </div>
        <div className="text-xs text-bh-muted mt-0.5 flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {sh.ship_to}</span>
          <span>{formatDateTime(sh.ship_date)}</span>
          {sh.tracking_number && (
            <span className="text-bh-blue font-mono inline-flex items-center gap-1">
              {sh.tracking_number} <ExternalLink className="w-3 h-3" />
            </span>
          )}
        </div>
      </div>
    </li>
  );
}

/* ---------- shared bits ---------- */
function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "morning";
  if (h < 18) return "afternoon";
  return "evening";
}

function EmptyState({ text }) {
  return <p className="text-center text-sm text-bh-muted py-10">{text}</p>;
}

function KpiTile({ icon: Icon, accent = "blue", label, value, sub, testId }) {
  const tones = {
    blue: "bg-bh-blue/10 text-bh-blue",
    orange: "bg-bh-orange/15 text-bh-orange-dark",
    green: "bg-green-100 text-green-700",
    violet: "bg-violet-100 text-violet-700",
  };
  return (
    <div data-testid={testId} className="bg-white border border-bh-divider rounded-xl p-5 hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col min-h-[140px]">
      <div className="flex items-start justify-between">
        <p className="text-sm font-medium text-bh-muted">{label}</p>
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tones[accent]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <p className="text-3xl font-bold text-bh-navy-deep mt-2" style={{ fontFamily: "Manrope" }}>{value}</p>
      <div className="text-sm text-bh-muted mt-1.5 flex-1">{sub}</div>
    </div>
  );
}

function Delta({ pct, rangeLabel }) {
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${up ? "text-green-700" : "text-red-600"}`}>
      {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
      {Math.abs(pct)}% vs previous {rangeLabel}
    </span>
  );
}

function Card({ title, subtitle, rightSlot, testId, children }) {
  return (
    <div data-testid={testId} className="bg-white border border-bh-divider rounded-xl p-6">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <h2 className="text-lg font-bold text-bh-navy-deep" style={{ fontFamily: "Manrope" }}>{title}</h2>
          {subtitle && <p className="text-xs text-bh-muted mt-0.5">{subtitle}</p>}
        </div>
        {rightSlot}
      </div>
      {children}
    </div>
  );
}

function Chip({ children }) {
  return (
    <span className="inline-flex items-center px-3 py-1 rounded-full bg-bh-orange/15 text-bh-orange-dark text-xs font-semibold tracking-wide">
      {children}
    </span>
  );
}
